import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../../components/base/Button';
import OrderDetailsTable from '../../components/tables/OrderDetailsTable';
import { Alert, Card, Col, Dropdown, Row, Badge, ProgressBar } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import OrderDetailsSummaryCard from '../../components/cards/OrderDetailsSummaryCard';
import {
  BillingDetails,
  OtherDetails,
  ShippingDetails
} from '../../components/sales-management/OrderDeliveryDetails';
import {
  faChevronDown,
  faPrint,
  faUndo,
  faDiagramProject
} from '@fortawesome/free-solid-svg-icons';
import { useOrder } from '../../features/orders/useOrders';
import OrderMaterialsOverview from '../../components/charts/sales-management-charts/OrderMaterialsOverview';
import OrderTraceability from '../../components/sales-management/OrderTraceability';
import EntityEventTimeline from '../../components/timelines/EntityEventTimeline';
import OrderLifecycleCard from '../../components/sales-management/OrderLifecycleCard';

const fmt = n => Number(n || 0).toLocaleString();

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: order = [],
    isLoading,
    isError,
    error,
    refetch
  } = useOrder(id);
  if (isLoading) return <p>Loading...</p>;
  // React cannot render an Error object, and react-query hands back the axios
  // Error itself — so `{error}` did not show the message, it threw
  // "Objects are not valid as a React child" and took the page down with it.
  // The failure the user was actually looking at then became invisible.
  if (isError) {
    const message =
      error?.response?.data?.error ||
      error?.message ||
      'Could not load this order';
    return (
      <Alert variant="danger" className="mt-3">
        {message}
      </Alert>
    );
  }

  const boardId = order.orderId || id;
  const lines = Array.isArray(order.lines) ? order.lines : [];
  const totalM = lines.reduce(
    (s, l) => s + (Number(l.totalOrderedLength) || 0),
    0
  );
  const producedM = lines.reduce(
    (s, l) => s + (Number(l.producedLength) || 0),
    0
  );
  const pct =
    totalM > 0
      ? Math.min(100, Math.round((producedM / totalM) * 100))
      : Number(order.progress) || 0;

  return (
    <div>
      <div className="mb-9">
        <h2 className="mb-1">
          Order <span> {order._id}</span>
        </h2>
        <div className="d-flex flex-wrap flex-between-center mb-4 gap-2">
          <p className="text-800 lh-sm mb-0">
            Customer ID :{' '}
            <Link className="fw-bold" to="#!">
              {' '}
              {order.client}
            </Link>
          </p>
          <div className="d-flex gap-5">
            <Button
              variant="link"
              className="p-0 text-900 text-decoration-none"
              startIcon={<FontAwesomeIcon icon={faPrint} className="me-2" />}
            >
              Print
            </Button>
            <Button
              variant="link"
              className="p-0 text-900 text-decoration-none"
              startIcon={<FontAwesomeIcon icon={faUndo} className="me-2" />}
            >
              Refund
            </Button>
            <Dropdown>
              <Dropdown.Toggle
                variant=""
                className="p-0 dropdown-caret-none text-decoration-none"
              >
                More action
                <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item href="#/action-1">Action</Dropdown.Item>
                <Dropdown.Item href="#/action-2">Another action</Dropdown.Item>
                <Dropdown.Item href="#/action-3">Something else</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <Row className="g-5 gy-7">
          <Col xs={12} xl={8} xxl={9}>
            <div className="mb-2">
              <OrderDetailsTable order={order} onChanged={refetch} />
            </div>
            <Row className="gx-4 gy-6 g-xl-7 justify-content-sm-center justify-content-xl-start">
              <Col xs={12}>
                <OrderTraceability order={order} />
              </Col>
              <Col xs={12}>
                <OrderMaterialsOverview order={order} />
              </Col>
            </Row>
          </Col>
          <Col xs={12} xl={4} xxl={3}>
            <OrderDetailsSummaryCard order={order} className="mb-4" />
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title as="h4" className="mb-0">
                    Production
                  </Card.Title>
                  {order.status && (
                    <Badge bg="info" variant="phoenix">
                      {String(order.status).toUpperCase()}
                    </Badge>
                  )}
                </div>

                <div className="mb-3">
                  {[
                    ['Order', order.orderCode || order._id],
                    ['Client', order.client],
                    [
                      'Due date',
                      order.dueDate ? String(order.dueDate).slice(0, 10) : '—'
                    ],
                    ['Delivery lines', lines.length],
                    ['Total ordered', `${fmt(totalM)} m`],
                    ['Produced', `${fmt(producedM)} m`]
                  ].map(([l, v]) => (
                    <div
                      key={l}
                      className="d-flex justify-content-between align-items-center py-1 border-bottom border-translucent"
                    >
                      <span className="text-muted fs-9">{l}</span>
                      <span className="fw-semibold fs-9 text-truncate ms-2">
                        {v ?? '—'}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="d-flex justify-content-between fs-9 text-muted mb-1">
                  <span>Overall production</span>
                  <span>
                    {fmt(producedM)} / {fmt(totalM)} m · {pct}%
                  </span>
                </div>
                <ProgressBar
                  now={pct}
                  variant="success"
                  style={{ height: 8 }}
                  className="mb-3"
                />

                <Button
                  variant="primary"
                  className="w-100"
                  startIcon={
                    <FontAwesomeIcon icon={faDiagramProject} className="me-2" />
                  }
                  onClick={() =>
                    navigate(`/apps/sales-management/reservations/${boardId}`)
                  }
                >
                  Open production board
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col xs={12}>
            <OrderLifecycleCard order={order} />
          </Col>
        </Row>

        {/* Everything that ever happened to this order: what was launched
            against its lines, who edited it, what was reserved from stock. */}
        <Row className="g-3 mt-1">
          <Col xs={12}>
            <EntityEventTimeline
              orderId={boardId}
              title="Order history"
              subtitle="Launches made for this order, edits, reservations and deletions — newest first."
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default OrderDetails;
